import type { Mode } from "./index.js";

// Missions as steps (Review 17: D45, C63) and tools that unlock lesson by lesson (D46).
// Plain data and pure functions, shared by the lessons (packages/assignments), the Worker and the
// browser (apps/web imports this file as "@buildlab/shared/steps", never the package root).

/** A part of the workspace a step points at; the workspace outlines it while that step is current. */
export type StepPanel = "preview" | "helper" | "cards" | "code" | "files" | "map" | "tests" | "submit" | "checkpoints";

/** What the student does that ticks a step. The browser observes these; nothing is graded on them. */
export type StepDone =
  /** Played the game (time with the preview in use, counted while this step is current). Default 3 s. */
  | { kind: "played"; seconds?: number }
  /** Sent the helper messages (optionally in one mode) while this step is current. Default 1. */
  | { kind: "sent"; mode?: Mode; min?: number }
  /** Approved a plan (Plan mode → Approve and build). */
  | { kind: "approvedPlan" }
  /** Opened a tab or view (Cards, Code, Files, Project map). */
  | { kind: "openedPanel"; panel: StepPanel }
  /** Answered the "Press Play" question before playing a given program (D47). */
  | { kind: "predicted" }
  /** Changed a file by hand (optionally one file) in Code or the notes. */
  | { kind: "edited"; file?: string }
  /** Ran Check my tests to the end. */
  | { kind: "checkedTests" }
  /** Made a save point by hand. */
  | { kind: "savePoint" }
  /** Submitted the exit ticket. */
  | { kind: "submitted" }
  /** The student ticks it ("Done"), for things the app can't see. */
  | { kind: "manual" };

/** One step of a lesson's mission: one action, at a 6th-grade reading level or below. */
export interface MissionStep {
  id: string;
  text: string;
  /** The panel this step lights up. */
  panel?: StepPanel;
  done: StepDone;
}

/** "Predict before you play" (D47, PRIMM): one question with 3 answers to tap before a given program runs. Nothing is graded. */
export interface Prediction {
  question: string;
  choices: string[];
}

/** Something the student did that might tick the current step (StepDone, as it happened). */
export type StepEvent =
  | { kind: "played"; seconds: number }
  | { kind: "sent"; mode: Mode }
  | { kind: "approvedPlan" }
  | { kind: "openedPanel"; panel: StepPanel }
  | { kind: "predicted" }
  | { kind: "edited"; file: string }
  | { kind: "checkedTests" }
  | { kind: "savePoint" }
  | { kind: "submitted" }
  | { kind: "manual" };

/** Where a student is in a mission: the steps done (in order) and counts toward the current one. */
export interface StepProgress {
  done: string[];
  /** Seconds played and messages sent since the current step became current. */
  played: number;
  sent: number;
  /** The counts are toward a level-up (decision 101), not a step. */
  levelUps?: boolean;
}

export const freshProgress = (done: string[] = []): StepProgress => ({ done, played: 0, sent: 0 });

/** The first step that isn't done (steps tick in order), or null when the mission is done. */
export function currentStep<S extends MissionStep>(steps: S[], done: string[]): S | null {
  return steps.find((s) => !done.includes(s.id)) ?? null;
}

/**
 * Applies one event to the current step only, so "Play again" after a bug report needs new play.
 * Returns the new progress and the id of the step it ticked, if any. Pure.
 */
export function applyStepEvent(steps: MissionStep[], p: StepProgress, e: StepEvent): { progress: StepProgress; ticked: string | null } {
  const step = currentStep(steps, p.done);
  if (!step) return { progress: p, ticked: null };
  const d = step.done;
  let played = p.played;
  let sent = p.sent;
  let hit = false;
  if (d.kind === "played" && e.kind === "played") {
    played += Math.max(0, e.seconds);
    hit = played >= (d.seconds ?? 3);
  } else if (d.kind === "sent" && e.kind === "sent") {
    if (!d.mode || d.mode === e.mode) {
      sent += 1;
      hit = sent >= (d.min ?? 1);
    }
  } else if (d.kind === "openedPanel" && e.kind === "openedPanel") hit = d.panel === e.panel;
  else if (d.kind === "edited" && e.kind === "edited") hit = !d.file || d.file === e.file;
  else hit = d.kind === e.kind && d.kind !== "played" && d.kind !== "sent" && d.kind !== "openedPanel" && d.kind !== "edited";
  if (!hit) return { progress: { ...p, played, sent }, ticked: null };
  return { progress: freshProgress([...p.done, step.id]), ticked: step.id };
}

/**
 * Ticks current steps that are already true (a prediction made before its step came up), in order.
 * Returns the ids it ticked.
 */
export function autoTick(steps: MissionStep[], p: StepProgress, facts: { predicted: boolean }): { progress: StepProgress; ticked: string[] } {
  const ticked: string[] = [];
  let progress = p;
  for (;;) {
    const step = currentStep(steps, progress.done);
    if (!step || !(step.done.kind === "predicted" && facts.predicted)) break;
    ticked.push(step.id);
    progress = freshProgress([...progress.done, step.id]);
  }
  return { progress, ticked };
}

// ---- level-ups (decision 101, Review 22: D69/D70) ---------------------------------------------------
// The required steps take 2–4 minutes of a 40-minute class. Level-ups give the rest of the period a
// goal tied to the same skill: up to 3 extra steps per lesson, the same shape and done kinds as the
// steps, ticked by the same engine in order. They open once the steps are all done, skipped, or the
// project was submitted. They earn helper coins, never points: the exit ticket doesn't see them (D70).

/** At most this many level-ups per lesson. */
export const MAX_LEVEL_UPS = 3;

/**
 * Helper coins for each level-up done, once per student per level-up (the Worker keeps that rule).
 * 1 coin = 1 helper message (40 a day). A level-up takes about 2–4 messages (a plan, a fix, a bug
 * report), so 5 pays for it with a little left over: a reward that keeps them building, not a stash.
 * All 3 add 15 for that day, about what a teacher's "+10 today" gives, and the class budget still caps spend.
 */
export const LEVEL_UP_COINS = 5;

/** True when the lesson's level-ups are open: every step done, steps skipped, or the project submitted. */
export function levelUpsOpen(steps: MissionStep[], levelUps: MissionStep[] | undefined, s: { done: string[]; skipped: boolean; submitted: boolean }): boolean {
  if (!levelUps?.length) return false;
  return s.skipped || s.submitted || steps.every((x) => s.done.includes(x.id));
}

/**
 * applyStepEvent for a whole mission: the event goes to the steps, or to the level-ups once they're
 * open, never both (so the step that finishes the mission doesn't also tick a level-up, and a plan
 * approved for a step doesn't count for "approve a level 2 plan"). Counts toward the current one
 * start again when the list changes. Pure.
 */
export function applyMissionEvent(
  steps: MissionStep[],
  levelUps: MissionStep[] | undefined,
  p: StepProgress,
  e: StepEvent,
  s: { skipped: boolean; submitted: boolean },
): { progress: StepProgress; ticked: string | null } {
  const open = levelUpsOpen(steps, levelUps, { done: p.done, ...s });
  const start = !!p.levelUps === open ? p : { ...freshProgress(p.done), ...(open ? { levelUps: true } : {}) };
  const r = applyStepEvent(open ? levelUps! : steps, start, e);
  return { progress: open ? { ...r.progress, levelUps: true } : r.progress, ticked: r.ticked };
}

/** "Level ups 2/3" for teachers: how many of a lesson's level-ups are done, or null when it has none. */
export function levelUpCount(levelUps: MissionStep[] | undefined, done: string[]): { done: number; total: number } | null {
  if (!levelUps?.length) return null;
  return { done: levelUps.filter((l) => done.includes(l.id)).length, total: levelUps.length };
}

// ---- tools that unlock lesson by lesson (D46) ------------------------------------------------------

/** Every tool a lesson can switch on. Modes are tools too (Build, Ask, Plan). */
export type Tool =
  | "preview"
  | "helper"
  | "steps"
  | "build"
  | "ask"
  | "plan"
  | "cards"
  | "code"
  | "files"
  | "console"
  | "map"
  | "tests"
  | "notes"
  | "checkpoints"
  | "history"
  | "timelapse";

export const ALL_TOOLS: readonly Tool[] = ["preview", "helper", "steps", "build", "ask", "plan", "cards", "code", "files", "console", "map", "tests", "notes", "checkpoints", "history", "timelapse"];

/** Names and one-line pitches for the "New tool unlocked!" moment (grade 6 or below). */
export const TOOL_INFO: Record<Tool, { label: string; blurb: string }> = {
  preview: { label: "Game", blurb: "Your program runs here." },
  helper: { label: "Helper", blurb: "Tell the helper what to build." },
  steps: { label: "Mission steps", blurb: "Do one step at a time." },
  build: { label: "Build mode", blurb: "The helper changes your files." },
  ask: { label: "Ask mode", blurb: "Ask how your code works. Nothing changes." },
  plan: { label: "Plan mode", blurb: "Get a plan first. You approve it before anything changes." },
  cards: { label: "Cards", blurb: "See each part of your program as a card." },
  code: { label: "Code", blurb: "Read the real code and change it by hand." },
  files: { label: "Files", blurb: "See every file in your project." },
  console: { label: "Error details", blurb: "Read what went wrong, line by line." },
  map: { label: "Project map", blurb: "See your plan, your screens and your rules." },
  tests: { label: "Check my tests", blurb: "The helper plays your game to check each test." },
  notes: { label: "Project notes", blurb: "Write rules your helper always follows." },
  checkpoints: { label: "Save points", blurb: "Save your work, and go back if a change goes wrong." },
  history: { label: "History", blurb: "Play every version of your project." },
  timelapse: { label: "Time-lapse", blurb: "Watch your project grow, start to end." },
};
